import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import { getKundaliPrefill, type KundaliPrefill } from "@/lib/api/kundali";
import { getBirthDetails, type SavedBirthDetails } from "@/lib/last-birth-details";
import type { KundaliField } from "@/lib/analytics-kundali";
import { formatBirthDateDisplayInput } from "./KundaliFormCard.validation";

/** Read a reading_id from the URL (hash first — the reactivation nudge's
 * collect-inputs link carries it in the hash alongside intent/next_report — then
 * query as a fallback). Client-only. */
function readReadingIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const fromHash = new URLSearchParams(rawHash).get("reading_id");
  if (fromHash) return fromHash;
  return new URLSearchParams(window.location.search).get("reading_id");
}

/** Read LITERAL birth-detail fields the seeker already entered on a calculator
 * page (numerology / rashi / mangal-dosha / dasamsa), carried in the
 * collect-inputs link's hash (name / dob / time / place). Unlike `reading_id`
 * (a stored chart that needs a fetch), these are the exact values the seeker
 * just typed, so they apply with NO network call — removing the "why am I
 * re-entering what I just typed?" friction on the highest-CTR calculator pages.
 * The hash fragment stays client-side (never sent to the origin), so the DOB is
 * not written to any server log. Client-only.
 * @internal
 */
export function readDirectPrefillFromUrl(): Partial<KundaliPrefill> {
  if (typeof window === "undefined") return {};
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hp = new URLSearchParams(rawHash);
  const qp = new URLSearchParams(window.location.search);
  const get = (k: string) => (hp.get(k) ?? qp.get(k) ?? "").trim();
  const out: Partial<KundaliPrefill> = {};
  const name = get("name");
  const dob = get("dob");
  const time = get("time");
  const place = get("place");
  if (name) out.name = name;
  if (dob) out.birth_date = dob;
  if (time) out.birth_time = time;
  if (place) out.birth_place = place;
  return out;
}

export interface KundaliPrefillSetters {
  setName: Dispatch<SetStateAction<string>>;
  setBirthDate: Dispatch<SetStateAction<string>>;
  setBirthTime: Dispatch<SetStateAction<string>>;
  setBirthPlace: Dispatch<SetStateAction<string>>;
  setSelectedPlace: Dispatch<SetStateAction<PlaceResult | null>>;
  setRememberedPlace?: Dispatch<SetStateAction<PlaceResult | null>>;
  /**
   * The field the seeker opened from the shell. It already has focus with the
   * caret at the end, so filling it would glue their typing onto the saved
   * value ("AshaRavi"); saved details leave it alone.
   */
  skipSavedField?: KundaliField;
}

/** Apply a prefill payload to the form's state — each field only when present. */
function applyPrefill(p: Partial<KundaliPrefill>, s: KundaliPrefillSetters): void {
  if (p.name) s.setName(p.name);
  if (p.birth_date) s.setBirthDate(p.birth_date);
  if (p.birth_time) s.setBirthTime(p.birth_time);
  if (p.birth_place) s.setBirthPlace(p.birth_place);
  if (p.birth_place && p.latitude != null && p.longitude != null) {
    s.setSelectedPlace({
      geoname_id: 0,
      name: p.birth_place,
      display: p.birth_place,
      latitude: p.latitude,
      longitude: p.longitude,
      timezone: p.timezone || "",
      country: "",
      population: 0,
    });
  }
}

/** Keep whatever is already in the field; fill it only while it is empty. */
function fillIfEmpty(next: string) {
  return (current: string) => (current.trim() ? current : next);
}

/**
 * Reuse the seeker's own saved birth details (`ishvaram_birth_details`, which
 * this form already writes) in fields that are still empty. The place goes in
 * as text plus a remembered exact place for the submit to use while the text
 * is unchanged; the chip is NOT committed here, so the form's height does not
 * change after hydration (rule 58) on the pages that server-render it.
 */
function applySavedBirthDetails(d: SavedBirthDetails, s: KundaliPrefillSetters): void {
  const fills = (field: KundaliField) => s.skipSavedField !== field;
  if (d.name && fills("name")) s.setName(fillIfEmpty(d.name));
  if (d.birth_date && fills("birthDate")) {
    s.setBirthDate(fillIfEmpty(formatBirthDateDisplayInput(d.birth_date)));
  }
  if (d.birth_time && fills("birthTime")) s.setBirthTime(fillIfEmpty(d.birth_time));
  if (fills("birthPlace")) applySavedPlace(d, s);
}

function applySavedPlace(d: SavedBirthDetails, s: KundaliPrefillSetters): void {
  if (!d.birth_place) return;
  s.setBirthPlace(fillIfEmpty(d.birth_place));
  if (d.latitude == null || d.longitude == null || !d.timezone) return;
  s.setRememberedPlace?.({
    geoname_id: 0,
    name: d.birth_place,
    display: d.birth_place,
    latitude: d.latitude,
    longitude: d.longitude,
    timezone: d.timezone,
    country: "",
    population: 0,
  });
}

/**
 * One-tap re-entry: when the URL carries a `reading_id` (a reactivation-nudge
 * landing), prefill the birth-details form from the seeker's STORED chart so they
 * confirm + pay instead of re-typing. Additive + best-effort — a missing id or a
 * failed fetch leaves the form empty (exactly today's behavior), so it can never
 * regress the normal /free-kundali load (no reading_id → the effect no-ops before
 * any fetch). Runs once. No layout change → no CLS (rule 58); the fetch fires only
 * on nudge landings, never on organic loads (rule 62).
 */
export function useReadingPrefill({
  setName,
  setBirthDate,
  setBirthTime,
  setBirthPlace,
  setSelectedPlace,
  setRememberedPlace,
  skipSavedField,
}: KundaliPrefillSetters): void {
  const done = useRef(false);
  // The shell's field as it was at mount: the prefill runs once, and a later
  // prop change must not re-run the effect and cancel a reading fetch.
  const skipAtMount = useRef(skipSavedField);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const setters = {
      setName,
      setBirthDate,
      setBirthTime,
      setBirthPlace,
      setSelectedPlace,
      setRememberedPlace,
      skipSavedField: skipAtMount.current,
    };
    // 1. Literal fields the seeker carried over from a calculator page (no fetch).
    const direct = readDirectPrefillFromUrl();
    const hasDirect = Boolean(
      direct.name || direct.birth_date || direct.birth_time || direct.birth_place
    );
    if (hasDirect) applyPrefill(direct, setters);
    const readingId = readReadingIdFromUrl();
    // 3. Neither a carried-over form nor a stored reading: the seeker's own saved
    //    details from an earlier visit, in the fields still empty (C26).
    if (!hasDirect && !readingId) applySavedBirthDetails(getBirthDetails(), setters);
    // 2. reading_id → fetch the stored chart (reactivation-nudge landing).
    if (!readingId) return;
    let cancelled = false;
    getKundaliPrefill(readingId)
      .then((p) => {
        if (!cancelled && p) {
          applyPrefill(p, setters);
        }
      })
      .catch(() => {
        // best-effort: leave the form empty (today's behavior)
      });
    return () => {
      cancelled = true;
    };
  }, [setName, setBirthDate, setBirthTime, setBirthPlace, setSelectedPlace, setRememberedPlace]);
}
